/*
 * LeadQuiz — the blind spot checker
 *
 * A blind spot is not a room without a device in it. The in-home presentation
 * defines it precisely, and this walk follows that definition:
 *
 *   1. Rooms a smoke alarm cannot go. The kitchen, garage, attic, laundry and
 *      crawlspace are full of steam, dust and exhaust that set a smoke alarm
 *      off, so code does not put one there — and nothing else goes there
 *      either. A heat sensor is what covers them.
 *
 *   2. Threats a smoke alarm cannot detect at all. Carbon monoxide is invisible
 *      and has no smell. Water finds its way behind an appliance and FEMA puts
 *      mold on a damp surface within 24 to 48 hours. A smoke alarm is silent
 *      through both.
 *
 *   3. Oxygen. This is the one nobody knows and it is the heart of the
 *      presentation: the fire does not have to reach you. It competes with you
 *      for the air. 21% is normal, judgement is clouded by 10-12%, and 4-6% is
 *      fatal — which is why people are found at their own front door or partway
 *      down their basement stairs.
 *
 *   4. The people a single horn cannot reach. A standard alarm is one horn in
 *      one room. It does not reliably wake a sleeping child, an adult over 40,
 *      or anyone who took something to sleep.
 *
 *   5. Not knowing where. A horn says there is a fire. It does not say which
 *      room, so the search happens in the dark while the oxygen drops.
 *
 * Every question is answerable from the kitchen table with no homework, and
 * every one is followed immediately by the thing the visitor did not know. That
 * rhythm — ask, then reveal — is what the dealer does in person, and it is what
 * makes the free evaluation at the end look worth an hour.
 */

export const STEPS = [{
    eyebrow: 'What your alarms can see',
    icon: 'shield-check',
    id: 'detects',
    kind: 'multi',
    options: [
        { id: 'smoke', label: 'Smoke' },
        { id: 'co', label: 'Carbon monoxide' },
        { id: 'heat', label: 'Heat' },
        { id: 'water', label: 'Water leaks' },
        { id: 'unsure', label: "I'm not sure" }
    ],
    question: 'What do the alarms in your home detect?',
    reveal: answers => {
        let picked = answers.detects || [];
        if(['co', 'heat', 'water'].every(id => picked.includes(id))) {
            return "That's rarer than you'd think. Most homes have smoke and nothing else.";
        }
        return "Smoke alarms detect smoke. That's it. Carbon monoxide you can't see or smell, and it comes off cars, fireplaces, generators, the water heater in the garage. Heat sensors cover the rooms a smoke alarm can't go in. Water shows up as a stain on a ceiling weeks after the leak started. Your alarms don't make a sound for any of that."; 
    },
    subtitle: `This includes ceiling mounted alarms and any supplemental plug-in alarms you've added yourself.`
},{
    eyebrow: 'Rooms no smoke alarm can watch',
    icon: 'house-door-fill',
    id: 'rooms',
    kind: 'multi',
    options: [
        { id: 'kitchen', label: 'Kitchen' },
        { id: 'garage', label: 'Attached garage' },
        { id: 'attic', label: 'Attic' },
        { id: 'laundry', label: 'Laundry room' },
        { id: 'basement', label: 'Basement' },
        { id: 'utility', label: 'Utility or furnace room' },
        { id: 'crawlspace', label: 'Crawlspace' }
    ],
    question: 'Which of these does your home have?',
    reveal: () => "Those rooms have one thing in common. You can't put a smoke alarm in any of them. Steam, dust, exhaust, heat off a dryer. It would go off every week, so code says don't bother, and most builders don't put anything else in either. A heat sensor handles it. You set it to a temperature the room should never hit.",
    subtitle: "Tick every one you have. They're grouped together for a reason."
},{
    eyebrow: 'Where water gets out',
    icon: 'droplet-fill',
    id: 'water',
    kind: 'multi',
    options: [
        { id: 'water-heater', label: 'Water heater' },
        { id: 'washer', label: 'Washing machine' },
        { id: 'dishwasher', label: 'Dishwasher' },
        { id: 'hvac', label: 'HVAC or furnace' },
        { id: 'sump', label: 'Sump pump' },
        { id: 'fridge', label: 'Fridge with ice or water' },
        { id: 'upstairs-bath', label: 'A bathroom above a living space' }
    ],
    question: 'Which of these are in your home?',
    reveal: answers => {
        let count = (answers.water || []).length;
        let opening = count === 0
            ? 'Every home has a few of these.'
            : `That's ${count} place${count === 1 ? '' : 's'} water can get out.`;
        return `${opening} FEMA says mold starts growing on a damp surface inside 24 to 48 hours. A slow leak behind a washing machine doesn't announce itself. Usually the ceiling underneath finds it first.`;
    },
    subtitle: "Water damage is the insurance claim homeowners make most often. It's also the one nobody sees coming."
},{
    eyebrow: 'Who has to hear it',
    icon: 'moon-stars-fill',
    id: 'sleepers',
    kind: 'multi',
    options: [
        { id: 'young_children', label: 'Children under 12' },
        { id: 'teens', label: 'Teenagers' },
        { id: 'older_adult', label: 'Someone over 65' },
        { id: 'hearing', label: 'Someone hard of hearing' },
        { id: 'other_floor', label: 'Someone on a different floor' },
        { id: 'adults_only', label: 'Just us' }
    ],
    question: 'Who is asleep in the house at night?',
    reveal: () => "A horn doesn't reliably wake a sleeping child. It often doesn't wake an adult over 40 either, or anyone who's had a drink or taken something to sleep. Most fatal house fires happen at night. That isn't a coincidence. It's the whole problem.",
    subtitle: "Almost every fatal house fire happens while everyone's asleep."
},{
    eyebrow: 'Would you know where',
    icon: 'geo-alt-fill',
    id: 'location',
    kind: 'single',
    options: [
        { id: 'yes', label: "Yes, I'd know the room" },
        { id: 'no', label: "No, I'd have to go looking" },
        { id: 'unsure', label: "Honestly, I've never thought about it" }
    ],
    question: 'If an alarm sounded at 2am, would you know which room it came from?',
    reveal: () => "Here's the part nobody tells you. The fire doesn't have to reach you to kill you. It's using up the same air you are. Normal air is 21% oxygen. By 10 or 12% your judgement is clouded. At 4 to 6% it's over. That's why people get found at their own front door, or halfway down the basement stairs. They were awake. They just went the wrong way.",
    subtitle: "A standard alarm tells you there's a fire. It doesn't tell you where."
}];

/*
 * The blind spots themselves, in the visitor's own home.
 *
 * Written from what they told us rather than from a template, because the
 * specific answer is the persuasive part — "nothing is watching your garage,
 * attic and laundry room" lands where "consider heat sensors" does not.
 */
export const getBlindSpots = answers => {

    let spots = [];
    let multi = id => answers[id] || [];
    let labelsFor = (stepId, ids) => {
        let step = STEPS.find(entry => entry.id === stepId);
        return step.options.filter(option => ids.includes(option.id)).map(option => option.label.toLowerCase());
    };

    // rooms a smoke alarm cannot go
    let rooms = multi('rooms').filter(id => id !== 'none');
    if(rooms.length > 0) {
        let names = labelsFor('rooms', rooms);
        spots.push({
            body: `You can't put a smoke alarm in ${names.length > 1 ? 'any of them' : 'there'}, and most homes end up with nothing at all. A heat sensor covers each one instead.`,
            icon: 'house-door-fill',
            title: `Nothing is watching your ${names.slice(0, 3).join(', ')}${names.length > 3 ? ` and ${names.length - 3} more` : ''}`
        });
    }

    // threats no smoke alarm detects
    let detects = multi('detects');
    let missing = ['co', 'heat', 'water'].filter(id => detects.includes(id) === false);
    if(missing.length > 0 || detects.includes('unsure')) {
        spots.push({
            body: "Carbon monoxide has no smell. Heat sensors cover the rooms a smoke alarm can't go in. Water gets out behind an appliance and turns up weeks later. Your smoke alarms stay quiet through every one of those.",
            icon: 'shield-check',
            title: detects.includes('unsure')
                ? "You're not sure what your own alarms detect"
                : `Nothing in your home detects ${labelsFor('detects', missing).join(', ')}`
        });
    }

    // water
    let water = multi('water');
    if(water.length > 0) {
        spots.push({
            body: "FEMA says mold starts on a damp surface inside 24 to 48 hours. Nobody checks behind a washing machine. The ceiling underneath tends to find the leak first.",
            icon: 'droplet-fill',
            title: `${water.length} place${water.length === 1 ? '' : 's'} water can get out, and nothing is watching any of them`
        });
    }

    // the people a horn cannot reach
    let sleepers = multi('sleepers').filter(id => id !== 'adults_only');
    if(sleepers.length > 0) {
        spots.push({
            body: "One horn, out in a hallway, through a closed door. That's what has to reach them. Bed shakers exist because a horn on its own often doesn't.",
            icon: 'moon-stars-fill',
            title: `One horn has to wake ${labelsFor('sleepers', sleepers).join(', ')}`
        });
    }

    // where
    if(['no', 'unsure'].includes(answers.location)) {
        spots.push({
            body: "Normal air is 21% oxygen. Judgement goes at 10 or 12%. At 4 to 6% it's fatal. Every minute you spend looking for the fire is a minute spent in air that's running out.",
            icon: 'geo-alt-fill',
            title: "You'd be looking for the fire while the air runs out"
        });
    }

    return spots;
};

// What the dealer reads when the lead lands. Written to be read on a phone by
// somebody about to make a call.
export const getSummary = answers => {

    let lines = ['Blind spot checker completed on the website.', ''];

    STEPS.forEach(step => {
        let answer = answers[step.id];
        if(!answer || (Array.isArray(answer) && answer.length === 0)) {
            return;
        }
        let selected = Array.isArray(answer) ? answer : [answer];
        let labels = step.options.filter(option => selected.includes(option.id)).map(option => option.label);
        if(labels.length > 0) {
            lines.push(`${step.question} — ${labels.join(', ')}`);
        }
    });

    let spots = getBlindSpots(answers);
    lines.push('');
    lines.push(`Blind spots identified: ${spots.length}`);
    spots.forEach(spot => lines.push(`• ${spot.title}`));

    return lines.join('\n');
};

/*
 * Where the walkthrough is up to, kept outside the component.
 *
 * Somebody four questions in who hits escape, or clicks the scrim to look at
 * something behind the modal, has made a slip rather than a decision. Throwing
 * their answers away punishes a misclick, and it is the moment they were most
 * likely to come back. The modal still unmounts — this is only the place it
 * left off.
 *
 * Start over is the deliberate way to clear it, and the confirmation screen
 * clears it too, so the next visitor to that browser starts clean.
 */
let session = { answers: {}, index: -1 };

export const getSession = () => session;

export const saveSession = next => {
    session = { ...session, ...next };
};

export const clearSession = () => {
    session = { answers: {}, index: -1 };
};

const LeadQuiz = { clearSession, getBlindSpots, getSession, getSummary, saveSession, STEPS };

export default LeadQuiz;
